import type { Caractere, PalavraCanonica } from '../tipos';

/**
 * Normalização Unicode (§4.1): todo texto entra em NFC antes de qualquer
 * comparação. A forma canônica do tabuleiro é NFC + caixa-alta, porque a
 * validação ignora caixa (§4.1) e o tabuleiro renderiza em caixa-alta (§4.3).
 */

export function normalizarNFC(texto: string): string {
  return texto.normalize('NFC');
}

/**
 * Converte para a forma canônica. Este é o único construtor de
 * `PalavraCanonica` — nada mais deve fazer o cast.
 */
export function paraCanonica(texto: string): PalavraCanonica {
  return normalizarNFC(texto).toUpperCase() as PalavraCanonica;
}

export function paraCaractere(caractere: string): Caractere {
  return normalizarNFC(caractere).toUpperCase() as Caractere;
}

/**
 * Divide em caracteres visíveis. Itera por code point para que diacríticos
 * pré-compostos contem como um caractere só (§4.1).
 */
export function caracteres(texto: string): readonly string[] {
  return Array.from(normalizarNFC(texto));
}

/** Caracteres canônicos, prontos para ir ao tabuleiro. */
export function caracteresCanonicos(texto: string): readonly Caractere[] {
  return Array.from(paraCanonica(texto)) as Caractere[];
}

/**
 * Mesma palavra, sem diacrítico nenhum: `CORAÇÃO` vira `CORACAO`.
 *
 * É a exceção da Trama (§23.3). O resto do jogo compara letras com
 * diacrítico preservado (§4.3, `mesmaLetra`), e é assim que tem de ser numa
 * palavra que o jogador escolheu escrever. Num jogo de dedução a regra se
 * inverte: exigir o acento faria o amarelo mentir — o `A` estaria aceso por
 * causa de um `Ã` que ninguém tinha como adivinhar.
 *
 * NFD separa a letra da marca; a faixa `U+0300–U+036F` são as marcas
 * combinantes, e a cedilha está entre elas, então `Ç` também vira `C`.
 */
export function semDiacriticos(texto: string): string {
  return texto.normalize('NFD').replace(/[\u0300-\u036f]/gu, '');
}

/** Forma de comparação da Trama: caixa-alta e sem diacrítico. */
export function paraComparacao(texto: string): string {
  return semDiacriticos(normalizarNFC(texto)).toUpperCase();
}

export function tamanhoVisivel(texto: string): number {
  return caracteres(texto).length;
}

/** Chave estável de célula, usada pelo RNG e pelos mapas da grade. */
export function chaveCelula(coluna: number, linha: number): string {
  return `${coluna}:${linha}`;
}
